import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Validation from '../../../shared/Validation';
import classNames from 'classnames';
import FloatingError from '../../../shared/FloatingError/FloatingError';
import Header from '../../../shared/Header/Header';
import Button from '../../../shared/Button/Button';
import passwordIcon from './password_icon.png';
import imprivataIcon from '../Icons/imprivata.svg';
import lock from '../Icons/lock.svg';
import './UsernamePassword.scss';

/**
 * Username/Password prompt for login
 * @class UsernamePassword
 * @extends {Component}
 */
class UsernamePassword extends Component {
  state = {
    errors: '',
    showErrors: false,
    username: '',
    password: '',
    disableSubmit: true,
  };
  static propTypes = {
    /** Header image */
    headerImage: PropTypes.string,
    /** Header title */
    headerTitle: PropTypes.string,
    /** Function executed when password field changes. Typically password in
    state is updated. */
    onInputChange: PropTypes.func,
    /** Function to execute when form is submitted. */
    onSubmit: PropTypes.func,
    /** Text displayed on the submit button. */
    submitText: PropTypes.string,
    /** Is something loading? */
    isLoading: PropTypes.bool,
    /** Validation constraints object for validate.js
    (https://validatejs.org/#constraints) */
    validation: PropTypes.object,
    /** Errors to display inside of component from a parent component */
    displayErrors: PropTypes.arrayOf(PropTypes.string),
    /** Whether the browser should attempt to autoComplete the username field */
    autoComplete: PropTypes.bool,
    /** Whether the username field should automatically gain focus */
    autoFocus: PropTypes.bool,
    /** Whether the password field should automatically gain focus */
    autoFocusPassword: PropTypes.bool,
    /** The component to be used for logout */
    logoutComponent: PropTypes.object,
    /** Whether the username and password fields should have an error highlight */
    highlightFields: PropTypes.bool,
    /** The DOM tree/component to be used for the footer */
    footer: PropTypes.object,
    /** A username to populate the first field if present */
    initialUsername: PropTypes.string,
    /** A password to populate the second field if present */
    initialPassword: PropTypes.string,
    /** Enable submit button for EPCS error scenarios */
    enableSubmit: PropTypes.bool,
    /** Disable username input for EPCS error scenarios */
    disableUsernameInput: PropTypes.bool,
    /** Disable password input for EPCS error scenarios */
    disablePasswordInput: PropTypes.bool,
    /** Use Parkland custom UX */
    useCustomUX: PropTypes.bool,
  };
  static defaultProps = {
    isLoading: false,
    headerImage: passwordIcon,
    headerTitle: 'Log in',
    submitText: 'Log in',
    validation: {
      username: {
        presence: true,
        length: { minimum: 1, },
      },
      password: {
        presence: true,
        length: { minimum: 1, },
      },
    },
    displayErrors: [],
    autoComplete: true,
    autoFocus: false,
    autoFocusPassword: false,
    logoutComponent: null,
    onInputChange: () => void (0),
    onSubmit: () => void (0),
    highlightFields: false,
    enableSubmit: false,
    disableUsernameInput: false,
    disablePasswordInput: false,
    // Parkland custom UX
    useCustomUX: false,
  };
  constructor(props) {
    super();
    this.validation = new Validation(props.validation);
  }
  componentDidMount() {
    if (this.props.initialUsername != null) {
      this.usernameInput.value = this.props.initialUsername;
    }
    if (this.props.initialPassword != null) {
      this.passwordInput.value = this.props.initialPassword;
    }
  }
  errorHelp = (fieldName) => {
    const errors = this.validation.errorMessages();
    if (errors && this.state.showErrors) {
      const message = errors[fieldName];
      return (
        <span className='help-block'>{message}</span>
      );
    }
    return '';
  };
  changeInput = () => {
    const inputValues = {
      username: this.usernameInput.value.trim(),
      password: this.passwordInput.value,
    };
    this.validation.updateValues(inputValues);
    inputValues.disableSubmit = this.validation.hasOnlyRequiredErrors();
    inputValues.errors = this.validation.errorMessages();
    inputValues.showErrors = false;
    this.setState(inputValues);
    this.props.onInputChange(inputValues);
  };
  submitForm = (event) => {
    event.preventDefault();
    if (this.state.errors) {
      this.setState({ showErrors: true, });
    }
    else {
      this.setState({ showErrors: false, });
      this.props.onSubmit(this.state);
    }
  };
  render() {
    const formClassesUsername = classNames({
      'form-control': true,
      'form-error-outline': this.props.highlightFields,
    });
    const formClassesPassword = classNames({
      'form-control': true,
      'password': true,
      'form-error-outline': this.props.highlightFields,
    });
    return (
      <div className='CIDRA'>
        {this.props.useCustomUX ?
          (<Header />)
          :
          (
            <div className='Header-Box'>
              <img src={lock} className='Modality-Icon' alt='lock' />
              <div className='Header-Text'>
                <span>{this.props.headerTitle}</span>
              </div>
            </div>
          )
        }
        <div className='login-container'>
          <form
            id='UsernamePassword_form'
            className='form'
            data-testid='username-password-form'
            onSubmit={this.submitForm}
          >
            {this.props.displayErrors.length > 0 ?
              (
                <div>
                  <FloatingError
                    messages={this.props.displayErrors}
                    showDuration={0} />
                </div>
              )
              : null
            }
            <div className='form-group CIDRA-Field'>
              <input
                autoFocus={this.props.autoFocus}
                type='text'
                id='UsernamePassword_username_input'
                className={formClassesUsername}
                placeholder='Username'
                name='username'
                ref={(input) => {
                  this.usernameInput = input;
                }}
                disabled={this.props.disableUsernameInput}
                onChange={this.changeInput}
                autoComplete={this.props.autoComplete ? 'on' : 'off'}
              />
              {this.errorHelp('username')}
            </div>
            <div className='form-group CIDRA-Field'>
              <input
                autoFocus={this.props.autoFocusPassword}
                type='password'
                id='UsernamePassword_password_input'
                className={formClassesPassword}
                placeholder='Password'
                name='password'
                ref={(input) => {
                  this.passwordInput = input;
                }}
                disabled={this.props.disablePasswordInput}
                onChange={this.changeInput}
              />
              {this.errorHelp('password')}
            </div>
            <div className='form-group CIDRA-Field'>
              {this.props.useCustomUX ?
                (
                  <Button
                    id='UsernamePassword_submit_button'
                    disabled={this.state.disableSubmit && !this.props.enableSubmit}
                    onClick={this.submitForm}
                    submitText='Sign In'
                  />
                )
                :
                (
                  <button
                    id='UsernamePassword_submit_button'
                    className='btn btn-primary btn-block'
                    type='submit'
                    disabled={this.state.disableSubmit && !this.props.enableSubmit}
                    onClick={this.submitForm}
                  >
                    {this.props.submitText}
                  </button>
                )
              }
            </div>
          </form>
        </div >
        <hr className='Footer-hr' />
        <img className='Footer' src={imprivataIcon} alt='logo' />
      </div >
    );
  }
}

export default UsernamePassword;
